/**
 * Kendo UI Widget Class
 *
 * @example
 * // ES6 class inheritance
 * class MyWidget extends Widget {
 *     init(element, options) {
 *         super.init(element, options);
 *         // widget-specific initialization
 *     }
 * }
 *
 * @example
 * // Legacy extend pattern
 * const MyWidget = Widget.extend({
 *     init: function(element, options) {
 *         Widget.fn.init.call(this, element, options);
 *         // widget-specific initialization
 *     },
 *     options: {
 *         name: "MyWidget"
 *     }
 * });
 */

import { Observable } from "./observable";
import type {
    IWidgetOptions,
    IWidgetSize,
    IComponentTypes,
    IFeature,
    IFeatureMap
} from "../models/widget";
import type { IKendoJQuery } from "../models/kendo-jquery";
import { kendoJQueryService } from "../services/kendo-jquery.service";
import { domUtilsService } from "../services/dom-utils.service";
import { utilsService } from "../services/utils.service";
import { cssPropertiesService } from "../services/css-properties.service";
import { WidgetPropertyEntry } from "../models";
import { addWatermarkOverlayAndBanner } from "../../licensing";

// CSS property names that can be applied to widgets
const cssPropertiesNames = ["themeColor", "fillMode", "shape", "size", "rounded", "positionMode"];

// ARIA constants
const ARIA_LABELLEDBY = "aria-labelledby";
const ARIA_LABEL = "aria-label";
const LABELIDPART = "_label";

/**
 * Base Widget class for Kendo UI components.
 */
export class Widget<TOptions extends IWidgetOptions = IWidgetOptions> extends Observable {
    /**
     * Static reference to prototype (jQuery-like pattern used throughout Kendo)
     */
    static fn: Widget;

    /**
     * Maps feature names to widget options they implement.
     * Override in subclasses to enable feature validation.
     */
    static readonly featureMap?: IFeatureMap;

    /**
     * Static call method to support legacy pattern: Widget.call(this, element, options)
     * 
     * ES6 classes cannot be invoked with Function.prototype.call(), but some legacy
     * code uses `kendo.ui.Widget.call(this, element, options)` instead of the proper
     * `Widget.fn.init.call(this, element, options)` pattern.
     * 
     * By defining a static `call` method, we intercept these calls and route them
     * to the init method, maintaining backward compatibility.
     * 
     * @param thisArg - The context (this) to call init on
     * @param element - The DOM element to bind the widget to
     * @param options - Widget configuration options
     */
    static call(thisArg: any, element?: HTMLElement | IKendoJQuery, options?: Partial<IWidgetOptions>): void {
        Widget.fn.init.call(thisArg, element, options);
    }

    /**
     * Static apply method to support legacy pattern: Widget.apply(this, arguments)
     * 
     * Similar to call(), this intercepts Function.prototype.apply() calls.
     * 
     * @param thisArg - The context (this) to call init on
     * @param args - Array of arguments [element, options]
     */
    static apply(thisArg: any, args?: [HTMLElement | IKendoJQuery, Partial<IWidgetOptions>?] | IArguments): void {
        Widget.fn.init.apply(thisArg, args as any);
    }

    /**
     * The wrapped element for this widget
     */
    element!: IKendoJQuery;

    /**
     * The wrapper element (may differ from element for some widgets)
     */
    wrapper?: IKendoJQuery;

    /**
     * Widget options - defined on prototype, not instance.
     * Subclasses override this on their prototype via extend().
     */
    declare options: TOptions;

    /**
     * Default options for the widget
     */
    defaults?: Partial<IWidgetOptions>;

    /**
     * Component type variations
     */
    componentTypes?: IComponentTypes;

    /**
     * Array of event names this widget can trigger.
     * Defined on prototype, not instance.
     */
    declare events: string[];

    /**
     * Internal size cache
     */
    _size?: IWidgetSize;

    /**
     * Watermark overlay function (set when license validation fails)
     */
    _showWatermarkOverlay?: (element: HTMLElement) => void;

    /**
     * Features active on this widget instance.
     */
    _features: IFeature[] = [];

    /**
     * Constructor - receives element and options and calls init().
     * This mirrors the legacy extend() pattern where the constructor IS the init function.
     * 
     * @param element - The DOM element to bind the widget to
     * @param options - Widget configuration options
     */
    constructor(element?: HTMLElement | IKendoJQuery, options?: Partial<TOptions>) {
        super();
        // Call init with the provided arguments
        if (element !== undefined) {
            this.init(element, options);
        }
    }

    /**
     * Initialize the widget
     * @param element - The DOM element to bind to
     * @param options - Widget configuration options
     */
    init(element: HTMLElement | IKendoJQuery, options?: Partial<TOptions>): void {
        const that = this;

        // Check licensing
        that._showWatermarkOverlay = addWatermarkOverlayAndBanner;

        // Wrap element with KendoJQuery and bind handler
        const kendoJQuery = kendoJQueryService.getConstructor();
        that.element = kendoJQuery(element).handler(that) as IKendoJQuery;

        // Initialize Observable
        Observable.fn.init.call(that);

        // Handle dataSource special case - avoid deep cloning
        let dataSource = options ? (options as any).dataSource : null;
        let props: Partial<IWidgetOptions> | undefined;

        if (options) {
            props = (that.componentTypes || {})[options.componentType as string];
        }

        if (dataSource) {
            // Avoid deep cloning the data source
            options = kendoJQuery.extend({}, options, { dataSource: {} });
        }

        const userFeatures = options?.features;
        const protoFeatures = that.options?.features;
        const features: IFeature[] = userFeatures ?? protoFeatures ?? [];
        if (options) {
            delete options.features;
        }

        options = that.options = kendoJQuery.extend(true, {}, that.options, that.defaults, props || {}, options) as TOptions;

        // Restore dataSource reference
        if (dataSource) {
            (options as any).dataSource = dataSource;
        }

        options.features = features;
        that._features = features;
        that._validateFeatures(features);

        for (let i = 0; i < features.length; i++) {
            features[i].setup(that);
        }

        // Set data-role attribute if not present
        const roleAttr = domUtilsService.attr("role");
        if (!that.element.attr(roleAttr)) {
            that.element.attr(roleAttr, (options.name || "").toLowerCase());
        }

        // Store widget instance on element
        that.element.data("kendo" + options.prefix + options.name, that);

        // Bind events from options
        that.bind(that.events, options);
    }

    /**
     * Check if the element has a MVVM binding target
     * @returns true if the element has a bindingTarget
     */
    _hasBindingTarget(): boolean {
        return !!(this.element[0] as any).kendoBindingTarget;
    }

    /**
     * Set up tabindex on target element
     * @param target - Target element (defaults to wrapper)
     */
    _tabindex(target?: IKendoJQuery): void {
        target = target || this.wrapper;

        const element = this.element;
        const TABINDEX = "tabindex";
        const tabindex = target!.attr(TABINDEX) || element.attr(TABINDEX);

        element.removeAttr(TABINDEX);

        target!.attr(TABINDEX, !isNaN(tabindex as any) ? tabindex : 0);
    }

    /**
     * Update widget options
     * @param options - New options to merge
     */
    setOptions(options: Partial<TOptions>): void {
        this._clearCssClasses(options);
        this._setEvents(options);
        kendoJQueryService.getConstructor().extend(this.options, options);
        this._applyCssClasses();
    }

    /**
     * Internal method to update event bindings when options change
     * @param options - New options containing event handlers
     */
    _setEvents(options: Partial<TOptions>): void {
        const that = this;
        let idx = 0;
        const length = that.events.length;
        let e: string;

        for (; idx < length; idx++) {
            e = that.events[idx];
            if (that.options[e] && options[e]) {
                that.unbind(e, that.options[e]);
                if (that._events && that._events[e]) {
                    delete that._events[e];
                }
            }
        }

        that.bind(that.events, options);
    }

    /**
     * Handle resize events
     * @param force - Force resize even if size hasn't changed
     */
    resize(force?: boolean): void {
        const size = this.getSize();
        const currentSize = this._size;

        if (force || (size.width > 0 || size.height > 0) && (!currentSize || size.width !== currentSize.width || size.height !== currentSize.height)) {
            this._size = size;
            this._resize(size, force);
            this.trigger("resize", size);
        }
    }

    /**
     * Get current widget dimensions
     * @returns Size object with width and height
     */
    getSize(): IWidgetSize {
        return domUtilsService.dimensions(this.element);
    }

    /**
     * Get or set widget size
     * @param size - Optional size to set
     * @returns Current size if no argument, undefined if setting
     */
    size(size?: IWidgetSize): IWidgetSize | void {
        if (!size) {
            return this.getSize();
        } else {
            this.setSize(size);
        }
    }

    /**
     * Set widget size (override in subclasses)
     * @param size - Size to set
     */
    setSize(_size: IWidgetSize): void {
        // noop - override in subclasses
    }

    /**
     * Internal resize handler (override in subclasses)
     * @param size - New size
     * @param force - Whether resize was forced
     */
    _resize(_size: IWidgetSize, _force?: boolean): void {
        // noop - override in subclasses
    }

    _validateFeatures(features: IFeature[]): void {
        const featureMap = (this.constructor as typeof Widget).featureMap;
        if (!featureMap) { return; }

        const ctor = this.constructor as any;
        const defaults = ctor.options || ctor.prototype?.options;
        if (!defaults) { return; }

        const loadedNames: Record<string, boolean> = {};
        for (let i = 0; i < features.length; i++) {
            loadedNames[features[i].name] = true;
        }

        for (const featureName in featureMap) {
            if (loadedNames[featureName]) { continue; }
            const opts = featureMap[featureName];
            for (let i = 0; i < opts.length; i++) {
                const key = opts[i];
                if (this.options[key] !== undefined && this.options[key] !== defaults[key]) {
                    throw new Error(
                        `Option "${key}" requires the "${featureName}" feature. ` +
                        `Import it and add it to the features array.`
                    );
                }
            }
        }
    }

    /**
     * Destroy the widget and clean up resources
     */
    destroy(): void {
        const that = this;

        if (that._features) {
            for (let i = that._features.length - 1; i >= 0; i--) {
                const f = that._features[i];
                if (f.teardown) {
                    f.teardown(that);
                }
            }
        }

        that.element.removeData("kendo" + that.options.prefix + that.options.name);
        that.element.removeData("handler");
        that.unbind();
    }

    /**
     * Internal destroy method
     */
    _destroy(): void {
        this.destroy();
    }

    /**
     * Apply CSS classes based on widget options
     * @param element - Optional element to apply classes to
     */
    _applyCssClasses(element?: IKendoJQuery): void {
        const protoOptions = (this as any).__proto__.options;
        const options = this.options;
        const el = element || this.wrapper || this.element;
        const classes: string[] = [];
        let i: number;
        let prop: string;
        let widgetName: string;
        let widgetProperties: WidgetPropertyEntry;

        const cssProps = cssPropertiesService;

        widgetName = this.options._altname || protoOptions.name;
        widgetProperties = cssProps.propertyDictionary[widgetName];

        if (!cssProps || !widgetProperties) {
            return;
        }

        for (i = 0; i < cssPropertiesNames.length; i++) {
            prop = cssPropertiesNames[i];
            widgetName = this.options._altname || protoOptions.name;

            if ((prop in protoOptions) || (prop in options)) {
                classes.push(cssProps.getValidClass({
                    widget: widgetName,
                    propName: prop,
                    value: options[prop as keyof TOptions]
                }));
            }
        }

        el.addClass(classes.join(" "));
    }

    /**
     * Set up ARIA label for accessibility
     * @param target - Target element to apply ARIA attributes to
     */
    _ariaLabel(target: IKendoJQuery): void {
        const that = this;
        const inputElm = that.element;
        const inputId = inputElm.attr("id");
        const $ = kendoJQueryService.getConstructor();
        const labelElm = $("label[for=\"" + inputId + "\"]");
        const ariaLabel = inputElm.attr(ARIA_LABEL);
        const ariaLabelledBy = inputElm.attr(ARIA_LABELLEDBY);
        let labelId: string;

        if (target[0] === inputElm[0]) {
            return;
        }

        if (ariaLabel) {
            target.attr(ARIA_LABEL, ariaLabel);
        } else if (ariaLabelledBy) {
            target.attr(ARIA_LABELLEDBY, ariaLabelledBy);
        } else if (labelElm.length) {
            const guid = utilsService.guid();
            labelId = labelElm.attr("id") || that._generateLabelId(labelElm, inputId || guid);
            target.attr(ARIA_LABELLEDBY, labelId);
        }
    }

    /**
     * Clear CSS classes before updating options
     * @param newOptions - New options being applied
     * @param element - Optional element to clear classes from
     */
    _clearCssClasses(newOptions: Partial<IWidgetOptions>, element?: IKendoJQuery): void {
        const protoOptions = (this as any).__proto__.options;
        const currentOptions = this.options;
        const el = element || this.wrapper || this.element;
        let i: number;
        let prop: string;
        let widgetName: string;
        let widgetProperties: WidgetPropertyEntry;

        const cssProps = cssPropertiesService;

        widgetName = this.options._altname || protoOptions.name;
        widgetProperties = cssProps.propertyDictionary[widgetName];

        if (!cssProps || !widgetProperties) {
            return;
        }

        for (i = 0; i < cssPropertiesNames.length; i++) {
            prop = cssPropertiesNames[i];

            if (((prop in protoOptions) || (prop in currentOptions)) && newOptions.hasOwnProperty(prop)) {
                if (prop === "themeColor") {
                    el.removeClass(cssProps.getValidClass({
                        widget: widgetName,
                        propName: prop,
                        value: currentOptions[prop],
                        fill: currentOptions.fillMode
                    }));
                } else {
                    if (prop === "fillMode") {
                        el.removeClass(cssProps.getValidClass({
                            widget: widgetName,
                            propName: "themeColor",
                            value: currentOptions.themeColor,
                            fill: currentOptions.fillMode
                        }));
                    }

                    el.removeClass(cssProps.getValidClass({
                        widget: widgetName,
                        propName: prop,
                        value: currentOptions[prop]
                    }));
                }
            }
        }
    }

    /**
     * Generate a unique label ID for ARIA
     * @param label - Label element
     * @param inputId - Input element ID
     * @returns Generated label ID
     */
    _generateLabelId(label: IKendoJQuery, inputId: string): string {
        const labelId = inputId + LABELIDPART;

        label.attr("id", labelId);

        return labelId;
    }
}

// Set up static fn reference (jQuery-like pattern)
Widget.fn = Widget.prototype;

// Define options and events on prototype (like original Kendo extend pattern)
// This is critical for the legacy extend() pattern to work correctly
Widget.prototype.options = {
    prefix: ""
};
Widget.prototype.events = [];

// Re-export types for convenience
export type {
    IWidgetOptions,
    IWidgetSize,
    IComponentTypes,
    WidgetConstructor
} from "../models/widget";
